import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

export const ONBOARDING_KEY = "r4pro:onboarding_completed";
export const markOnboardingComplete = () => {
  try { localStorage.setItem(ONBOARDING_KEY, "true"); } catch { /* ignore */ }
};
export const clearOnboarding = () => {
  try { localStorage.removeItem(ONBOARDING_KEY); } catch { /* ignore */ }
};

/* ── Illustrations ─────────────────────────────────────────────────────────── */

function IllustrationHandshake() {
  return (
    <svg viewBox="0 0 320 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      {/* Blueprint grid subtle */}
      <defs>
        <pattern id="grid1" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M20 0H0V20" fill="none" stroke="currentColor" strokeOpacity="0.06" strokeWidth="0.6" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="320" height="220" fill="url(#grid1)" style={{ color: "#0F1013" }} />
      {/* Left building (factory) */}
      <g style={{ color: "#0F1013" }} fill="currentColor">
        <rect x="24" y="112" width="70" height="76" rx="2" opacity="0.14" />
        <path d="M24 112 L36 96 L48 112 Z" opacity="0.14" />
        <path d="M48 112 L60 96 L72 112 Z" opacity="0.14" />
        <path d="M72 112 L84 96 L96 112 Z" opacity="0.14" />
        <rect x="34" y="128" width="8" height="8" fill="#2E6BFF" opacity="0.85" />
        <rect x="50" y="128" width="8" height="8" fill="#2E6BFF" opacity="0.85" />
        <rect x="66" y="128" width="8" height="8" fill="#2E6BFF" opacity="0.85" />
        <rect x="34" y="146" width="8" height="8" fill="#2E6BFF" opacity="0.85" />
        <rect x="50" y="146" width="8" height="8" fill="#2E6BFF" opacity="0.85" />
        <rect x="66" y="146" width="8" height="8" fill="#2E6BFF" opacity="0.85" />
      </g>
      {/* Right building (office) */}
      <g style={{ color: "#0F1013" }} fill="currentColor">
        <rect x="226" y="96" width="70" height="92" rx="2" opacity="0.92" />
        <g fill="#2E6BFF" opacity="0.85">
          <rect x="234" y="106" width="8" height="8" /><rect x="250" y="106" width="8" height="8" /><rect x="266" y="106" width="8" height="8" /><rect x="282" y="106" width="8" height="8" />
          <rect x="234" y="122" width="8" height="8" /><rect x="250" y="122" width="8" height="8" /><rect x="266" y="122" width="8" height="8" /><rect x="282" y="122" width="8" height="8" />
          <rect x="234" y="138" width="8" height="8" /><rect x="250" y="138" width="8" height="8" /><rect x="266" y="138" width="8" height="8" /><rect x="282" y="138" width="8" height="8" />
          <rect x="234" y="154" width="8" height="8" /><rect x="250" y="154" width="8" height="8" /><rect x="266" y="154" width="8" height="8" /><rect x="282" y="154" width="8" height="8" />
        </g>
      </g>
      {/* Dashed connection arc */}
      <path d="M96 70 Q160 20 226 70" fill="none" stroke="#2E6BFF" strokeWidth="1.4" strokeDasharray="3 4" opacity="0.6" />
      {/* Checkmark badge */}
      <g transform="translate(160 42)">
        <circle r="12" fill="#2E6BFF" />
        <path d="M-4 0 L-1 3 L5 -3" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      {/* Two figures shaking hands */}
      <g transform="translate(160 148)">
        {/* Left person */}
        <g transform="translate(-42 0)">
          <circle cx="0" cy="-28" r="8" fill="#0F1013" />
          <path d="M-10 -18 Q-14 6 -8 30 L8 30 Q10 8 8 -18 Z" fill="#2E6BFF" />
          <rect x="-10" y="30" width="7" height="18" fill="#0F1013" />
          <rect x="3" y="30" width="7" height="18" fill="#0F1013" />
        </g>
        {/* Right person */}
        <g transform="translate(42 0)">
          <circle cx="0" cy="-28" r="8" fill="#0F1013" />
          <path d="M-10 -18 Q-14 6 -8 30 L8 30 Q10 8 8 -18 Z" fill="#1B1E24" />
          <rect x="-10" y="30" width="7" height="18" fill="#0F1013" />
          <rect x="3" y="30" width="7" height="18" fill="#0F1013" />
        </g>
        {/* Handshake bar */}
        <rect x="-18" y="-4" width="36" height="6" rx="2" fill="#0F1013" />
      </g>
    </svg>
  );
}

function IllustrationWorkflow() {
  const label = "text-[10px] font-medium";
  const labelStyle = { color: "#0F1013" };
  return (
    <svg viewBox="0 0 320 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      <defs>
        <pattern id="grid2" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M20 0H0V20" fill="none" stroke="currentColor" strokeOpacity="0.05" strokeWidth="0.6" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="320" height="220" fill="url(#grid2)" style={{ color: "#0F1013" }} />
      {/* Dashed orbit */}
      <circle cx="160" cy="110" r="72" fill="none" stroke="#2E6BFF" strokeOpacity="0.45" strokeWidth="1.2" strokeDasharray="3 4" />
      {/* Center R4 */}
      <g transform="translate(160 110)">
        <circle r="22" fill="#0F1013" />
        <text x="0" y="4" textAnchor="middle" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fontSize="12" fontWeight="700" fill="#fff">R4</text>
      </g>
      {/* Top: Richiesta (document) */}
      <g transform="translate(160 32)">
        <rect x="-14" y="-16" width="28" height="32" rx="4" fill="#fff" stroke="#0F1013" strokeWidth="1.4" />
        <line x1="-8" y1="-6" x2="8" y2="-6" stroke="#0F1013" strokeWidth="1.2" />
        <line x1="-8" y1="0"  x2="8" y2="0"  stroke="#0F1013" strokeWidth="1.2" />
        <line x1="-8" y1="6"  x2="4" y2="6"  stroke="#0F1013" strokeWidth="1.2" />
      </g>
      {/* Right: Chat */}
      <g transform="translate(238 110)">
        <path d="M-16 -12 h32 a4 4 0 0 1 4 4 v12 a4 4 0 0 1 -4 4 h-18 l-8 8 v-8 h-6 a4 4 0 0 1 -4 -4 v-12 a4 4 0 0 1 4 -4 z" fill="#fff" stroke="#0F1013" strokeWidth="1.4" />
        <circle cx="-6" cy="0" r="1.4" fill="#0F1013" /><circle cx="0" cy="0" r="1.4" fill="#0F1013" /><circle cx="6" cy="0" r="1.4" fill="#0F1013" />
      </g>
      {/* Bottom: Documenti (folder) */}
      <g transform="translate(160 188)">
        <path d="M-16 -12 h10 l4 4 h18 a2 2 0 0 1 2 2 v14 a2 2 0 0 1 -2 2 h-32 a2 2 0 0 1 -2 -2 v-18 a2 2 0 0 1 2 -2 z" fill="#fff" stroke="#0F1013" strokeWidth="1.4" />
      </g>
      {/* Left: Proposte (factory) */}
      <g transform="translate(82 110)">
        <rect x="-16" y="-4" width="32" height="18" fill="#fff" stroke="#0F1013" strokeWidth="1.4" />
        <path d="M-16 -4 L-16 -14 L-6 -8 L-6 -14 L4 -8 L4 -14 L16 -4 Z" fill="#fff" stroke="#0F1013" strokeWidth="1.4" />
        <rect x="-12" y="2" width="4" height="6" fill="#2E6BFF" />
        <rect x="-2"  y="2" width="4" height="6" fill="#2E6BFF" />
        <rect x="8"   y="2" width="4" height="6" fill="#2E6BFF" />
      </g>
      {/* Labels */}
      <foreignObject x="120" y="52" width="80" height="16"><div xmlns="http://www.w3.org/1999/xhtml" className={label} style={{ ...labelStyle, textAlign: "center" }}>Richiesta</div></foreignObject>
      <foreignObject x="212" y="132" width="60" height="16"><div xmlns="http://www.w3.org/1999/xhtml" className={label} style={{ ...labelStyle, textAlign: "center" }}>Chat</div></foreignObject>
      <foreignObject x="120" y="196" width="80" height="16"><div xmlns="http://www.w3.org/1999/xhtml" className={label} style={{ ...labelStyle, textAlign: "center" }}>Documenti</div></foreignObject>
      <foreignObject x="46" y="132" width="80" height="16"><div xmlns="http://www.w3.org/1999/xhtml" className={label} style={{ ...labelStyle, textAlign: "center" }}>Proposte</div></foreignObject>
    </svg>
  );
}

function IllustrationDevices() {
  return (
    <svg viewBox="0 0 320 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      <defs>
        <pattern id="grid3" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M20 0H0V20" fill="none" stroke="currentColor" strokeOpacity="0.05" strokeWidth="0.6" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="320" height="220" fill="url(#grid3)" style={{ color: "#0F1013" }} />
      {/* Laptop */}
      <g transform="translate(40 46)">
        <rect x="0" y="0" width="220" height="130" rx="8" fill="#0F1013" />
        <rect x="8" y="8" width="204" height="114" rx="4" fill="#F2ECD8" />
        {/* Sidebar */}
        <rect x="12" y="12" width="34" height="106" fill="#E8E0C6" />
        <rect x="18" y="20" width="22" height="4" rx="1" fill="#0F1013" opacity="0.35" />
        <rect x="18" y="30" width="22" height="4" rx="1" fill="#0F1013" opacity="0.2" />
        <rect x="18" y="40" width="22" height="4" rx="1" fill="#0F1013" opacity="0.2" />
        {/* Header */}
        <rect x="52" y="14" width="150" height="8" rx="2" fill="#0F1013" opacity="0.15" />
        {/* Rows */}
        <g>
          <rect x="52" y="30" width="150" height="18" rx="3" fill="#fff" stroke="#DFD5B2" />
          <rect x="58" y="36" width="70" height="6" rx="1.5" fill="#0F1013" opacity="0.55" />
          <rect x="180" y="36" width="16" height="6" rx="1.5" fill="#2E6BFF" />
        </g>
        <g>
          <rect x="52" y="54" width="150" height="18" rx="3" fill="#fff" stroke="#DFD5B2" />
          <rect x="58" y="60" width="90" height="6" rx="1.5" fill="#0F1013" opacity="0.55" />
          <rect x="180" y="60" width="16" height="6" rx="1.5" fill="#2E6BFF" />
        </g>
        <g>
          <rect x="52" y="78" width="150" height="18" rx="3" fill="#fff" stroke="#DFD5B2" />
          <rect x="58" y="84" width="80" height="6" rx="1.5" fill="#0F1013" opacity="0.55" />
          <rect x="180" y="84" width="16" height="6" rx="1.5" fill="#2E6BFF" />
        </g>
        {/* Base */}
        <path d="M-8 130 L228 130 L216 138 L4 138 Z" fill="#0F1013" opacity="0.9" />
      </g>
      {/* Phone */}
      <g transform="translate(212 74)">
        <rect x="0" y="0" width="72" height="130" rx="12" fill="#0F1013" />
        <rect x="4" y="6" width="64" height="118" rx="8" fill="#F2ECD8" />
        <rect x="26" y="8" width="20" height="4" rx="2" fill="#0F1013" opacity="0.5" />
        {/* Chat bubbles */}
        <rect x="10" y="22" width="46" height="12" rx="4" fill="#fff" stroke="#DFD5B2" />
        <rect x="18" y="40" width="46" height="12" rx="4" fill="#2E6BFF" />
        <rect x="10" y="58" width="40" height="12" rx="4" fill="#fff" stroke="#DFD5B2" />
        <rect x="18" y="76" width="46" height="12" rx="4" fill="#2E6BFF" />
        <rect x="10" y="106" width="52" height="10" rx="3" fill="#fff" stroke="#DFD5B2" />
      </g>
    </svg>
  );
}

/* ── Slide component ───────────────────────────────────────────────────────── */

function highlight(text, wordsToBlue) {
  // Renders `text` with any words in `wordsToBlue` painted electric-500.
  const parts = text.split(/(\s+)/);
  return parts.map((p, i) => {
    const clean = p.replace(/[.,!?]/g, "");
    const isBlue = wordsToBlue.some((w) => w.toLowerCase() === clean.toLowerCase());
    return (
      <span key={i} className={isBlue ? "text-electric-500" : undefined}>
        {p}
      </span>
    );
  });
}

function Slide({ title, subtitle, blueWords, illustration, testId }) {
  return (
    <div className="w-full flex-shrink-0 flex flex-col items-center px-6 sm:px-10 pt-6 pb-10" data-testid={testId}>
      <div className="w-full max-w-md mx-auto">
        <div
          className="rounded-2xl p-5 sm:p-6"
          style={{ background: "#FFFFFF", border: "1px solid #E4DFCD", boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 24px -16px rgba(16,24,40,0.08)" }}
        >
          <div
            className="aspect-[16/11] rounded-xl overflow-hidden"
            style={{ background: "#F7F5EE", border: "1px solid #E4DFCD" }}
          >
            {illustration}
          </div>
        </div>
        <h2 className="mt-8 text-2xl sm:text-3xl font-semibold text-center leading-tight" style={{ color: "#0F1013" }}>
          {highlight(title, blueWords)}
        </h2>
        <p className="mt-4 text-sm text-center leading-relaxed whitespace-pre-line" style={{ color: "#4B5060" }}>
          {subtitle}
        </p>
      </div>
    </div>
  );
}

/* ── Main page ─────────────────────────────────────────────────────────────── */

export default function Onboarding() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const trackRef = useRef(null);
  const touchStartX = useRef(null);

  const slides = useMemo(() => ([
    {
      title: t("onboarding.s1.title"),
      subtitle: t("onboarding.s1.subtitle"),
      blueWords: t("onboarding.s1.blueWords").split("|"),
      illustration: <IllustrationHandshake />,
      testId: "onboarding-slide-1",
    },
    {
      title: t("onboarding.s2.title"),
      subtitle: t("onboarding.s2.subtitle"),
      blueWords: t("onboarding.s2.blueWords").split("|"),
      illustration: <IllustrationWorkflow />,
      testId: "onboarding-slide-2",
    },
    {
      title: t("onboarding.s3.title"),
      subtitle: t("onboarding.s3.subtitle"),
      blueWords: t("onboarding.s3.blueWords").split("|"),
      illustration: <IllustrationDevices />,
      testId: "onboarding-slide-3",
    },
  ]), [t]);

  const complete = () => {
    markOnboardingComplete();
    navigate("/app/dashboard", { replace: true });
  };
  const skip = () => complete();
  const next = () => {
    if (index < slides.length - 1) setIndex((i) => i + 1);
    else complete();
  };
  const prev = () => setIndex((i) => Math.max(0, i - 1));

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "Escape") skip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index]);

  // Swipe
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) next(); else prev();
  };

  const isLast = index === slides.length - 1;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "#F7F5EE", color: "#0F1013" }}
      data-testid="onboarding-root"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Top bar */}
      <header className="flex items-center justify-between px-6 sm:px-10 pt-6">
        <div className="leading-tight">
          <div className="text-xl font-extrabold tracking-tight" style={{ color: "#2E6BFF" }}>R4Pro</div>
          <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#8A8878" }}>Manufacturing Finder</div>
        </div>
        {!isLast && (
          <button
            onClick={skip}
            data-testid="onboarding-skip"
            className="text-sm transition flex items-center gap-1"
            style={{ color: "#4B5060" }}
          >
            {t("onboarding.skip")}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
        {isLast && (
          <button
            onClick={skip}
            data-testid="onboarding-skip"
            className="text-sm transition"
            style={{ color: "#8A8878" }}
          >
            {t("onboarding.skip")}
          </button>
        )}
      </header>

      {/* Slides */}
      <main className="flex-1 flex items-center overflow-hidden">
        <div
          ref={trackRef}
          className="flex w-full transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {slides.map((s, i) => (
            <div key={i} className="w-full flex-shrink-0">
              <Slide {...s} />
            </div>
          ))}
        </div>
      </main>

      {/* Footer: dots + primary action on last slide */}
      <footer className="px-6 sm:px-10 pb-8 pt-2">
        <div className="max-w-md mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-2" data-testid="onboarding-dots">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                data-testid={`onboarding-dot-${i}`}
                className="h-1.5 rounded-full transition-all"
                style={{
                  width: i === index ? "24px" : "6px",
                  background: i === index ? "#2E6BFF" : "#D6D2C4",
                }}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
          {isLast ? (
            <button
              onClick={complete}
              data-testid="onboarding-start"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-medium transition"
              style={{ background: "#2E6BFF", boxShadow: "0 2px 10px -2px rgba(46,107,255,0.55)" }}
            >
              {t("onboarding.start")}
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={next}
              data-testid="onboarding-next"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition"
              style={{ color: "#0F1013", border: "1px solid #E4DFCD", background: "#FFFFFF" }}
            >
              {t("onboarding.next")}
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}
